import { useState, useRef, useEffect } from "react";
import config from "./config";

// "GIF" button + popover that searches GIPHY (trending by default) and calls
// onPick(gifUrl) with a hosted GIF URL. Dependency-free (uses fetch).
export default function GifPicker({ onPick, className = "", dropUp = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [gifs, setGifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounce = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const fetchGifs = (query) => {
    setLoading(true);
    const base = "https://api.giphy.com/v1/gifs";
    const url = query
      ? `${base}/search?api_key=${config.GIPHY_KEY}&q=${encodeURIComponent(query)}&limit=24&rating=pg-13&bundle=fixed_width`
      : `${base}/trending?api_key=${config.GIPHY_KEY}&limit=24&rating=pg-13&bundle=fixed_width`;
    fetch(url)
      .then((r) => r.json())
      .then((d) => {
        setGifs(
          (d.data || [])
            .map((g) => ({
              id: g.id,
              preview: g.images?.fixed_width?.url || g.images?.downsized?.url,
              full: g.images?.downsized_medium?.url || g.images?.fixed_width?.url,
            }))
            .filter((g) => g.preview && g.full)
        );
      })
      .catch(() => setGifs([]))
      .finally(() => setLoading(false));
  };

  // Load trending whenever the picker opens.
  useEffect(() => {
    if (open) fetchGifs(q.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSearch = (val) => {
    setQ(val);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchGifs(val.trim()), 350);
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Add gif"
        className="flex items-center justify-center text-xs font-bold tracking-wide text-gray-500 hover:text-gray-700 transition"
      >
        GIF
      </button>

      {open && (
        <div className={`fixed inset-x-3 bottom-3 z-50 w-auto sm:absolute sm:z-40 sm:inset-x-auto sm:right-0 sm:w-[320px] ${dropUp ? "sm:top-auto sm:bottom-full sm:mb-2" : "sm:bottom-auto sm:top-full sm:mt-2"} rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden`}>
          {/* Search */}
          <div className="p-2.5 border-b border-gray-100">
            <div className="relative">
              <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.3-4.3M11 18a7 7 0 100-14 7 7 0 000 14z" />
              </svg>
              <input
                value={q}
                onChange={(e) => onSearch(e.target.value)}
                placeholder="Search GIFs"
                className="w-full text-sm bg-gray-100 rounded-lg pl-9 pr-3 py-2 outline-none focus:bg-gray-50 placeholder:text-gray-400"
              />
            </div>
          </div>

          {/* Results (2-column masonry) */}
          <div className="max-h-72 overflow-y-auto p-2">
            {loading ? (
              <p className="py-12 text-center text-sm text-gray-400">Loading…</p>
            ) : gifs.length === 0 ? (
              <p className="py-12 text-center text-sm text-gray-400">No GIFs found</p>
            ) : (
              <div className="columns-2 gap-2">
                {gifs.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => {
                      onPick(g.full);
                      setOpen(false);
                    }}
                    className="mb-2 block w-full overflow-hidden rounded-lg bg-gray-100 hover:opacity-90 transition"
                  >
                    <img src={g.preview} alt="" loading="lazy" className="w-full" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 px-3 py-1.5 text-center text-[10px] font-medium uppercase tracking-wider text-gray-400">
            Powered by GIPHY
          </div>
        </div>
      )}
    </div>
  );
}
